/**
 * Estado de conectividade do app — UMA fonte para a faixa de rede (BUG-260).
 *
 * Duas perguntas diferentes, que a faixa antiga confundia numa só:
 *
 *  1. **O navegador afirma offline?** (`navigator.onLine === false`). Certeza
 *     de offline; nada a tentar — a rede volta quando volta.
 *  2. **O backend responde?** O navegador diz "conectado" e o Firestore não
 *     consegue abrir o canal: Wi-Fi sem rota, 3G que não passa pacote, o
 *     transporte zumbi do BUG-179/196. Aqui `navigator.onLine` é `true` e a
 *     faixa antiga ficava MUDA — a pessoa via skeleton e "salvando" eterno sem
 *     explicação. É o cenário do relato de 16/09: *"com a conexão ruim o app
 *     não entra; só quando a internet está totalmente desabilitada ele entra"*.
 *
 * A resposta da 2ª vem da sonda (`BackendReachabilityProbe`): o listener do
 * perfil com `includeMetadataChanges` — `fromCache: true` que PERSISTE por
 * `BACKEND_GRACE_MS` significa que o SDK não conseguiu o servidor. O prazo de
 * graça existe porque todo boot começa `fromCache: true` por um ou dois
 * segundos antes de o servidor confirmar; acender a faixa nesse instante seria
 * a faixa mentindo com a rede boa, que é pior que faixa ausente.
 *
 * `episode` conta as vezes que a faixa ACENDEU (transição de `online` para
 * qualquer outro estado). É o que o X de dispensar guarda: dispensar vale para
 * este episódio, e um episódio novo mostra a faixa de novo — dispensar não pode
 * significar "nunca mais me avise", porque aí o aviso deixa de existir.
 *
 * `useSyncExternalStore` pela mesma razão do `useOnlineStatus`: a regra
 * `react-hooks/set-state-in-effect` está ativa, e o snapshot é um objeto
 * ESTÁVEL (só troca de referência quando um campo muda).
 */
import { useSyncExternalStore } from "react";
import { readOnlineStatus } from "../../hooks/useOnlineStatus";

export type ConnectivityStatus = "online" | "offline" | "unstable";

export interface ConnectivitySnapshot {
  /** Veredito combinado: `offline` ganha de `unstable`. */
  status: ConnectivityStatus;
  /** `navigator.onLine` (ou `true` quando não há como saber). */
  browserOnline: boolean;
  /** `false` só depois de `fromCache` persistir além do prazo de graça. */
  backendReachable: boolean;
  /** Quantas vezes a faixa acendeu nesta abertura do app. */
  episode: number;
}

/**
 * Quanto tempo `fromCache: true` precisa persistir antes de virar "instável".
 * Um boot com rede boa confirma o servidor em ~1–2 s; rede de celular lenta
 * pode levar 3–4 s. 6 s deixa a lenta em paz e ainda pega o "nunca".
 */
export const BACKEND_GRACE_MS = 6_000;

type Listener = () => void;

let browserOnline = readOnlineStatus();
let backendReachable = true;
let episode = 0;
let snapshot: ConnectivitySnapshot = derive();
let graceTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<Listener>();
let browserListening = false;

function derive(): ConnectivitySnapshot {
  const status: ConnectivityStatus = !browserOnline
    ? "offline"
    : !backendReachable
      ? "unstable"
      : "online";
  return { status, browserOnline, backendReachable, episode };
}

function commit() {
  const next = derive();
  if (
    next.status === snapshot.status &&
    next.browserOnline === snapshot.browserOnline &&
    next.backendReachable === snapshot.backendReachable
  ) {
    return;
  }
  // Acendeu (ou trocou de motivo sem apagar): episódio novo. Trocar de motivo
  // conta porque a copy muda — "sem conexão" dispensada não cobre "instável".
  if (next.status !== "online" && next.status !== snapshot.status) {
    episode += 1;
    next.episode = episode;
  }
  snapshot = next;
  for (const l of listeners) l();
}

function onBrowserChange() {
  browserOnline = readOnlineStatus();
  commit();
}

function ensureBrowserListener() {
  if (browserListening || typeof window === "undefined") return;
  browserListening = true;
  window.addEventListener("online", onBrowserChange);
  window.addEventListener("offline", onBrowserChange);
}

/**
 * A sonda reporta cada snapshot com metadados. `fromCache: false` cura na hora;
 * `fromCache: true` só condena depois do prazo — e um `false` no meio cancela.
 */
export function reportBackendSnapshot(fromCache: boolean): void {
  if (!fromCache) {
    if (graceTimer !== undefined) {
      clearTimeout(graceTimer);
      graceTimer = undefined;
    }
    backendReachable = true;
    commit();
    return;
  }
  if (!backendReachable || graceTimer !== undefined) return;
  graceTimer = setTimeout(() => {
    graceTimer = undefined;
    backendReachable = false;
    commit();
  }, BACKEND_GRACE_MS);
}

/**
 * A sonda saiu (logout, unmount): sem sonda não há veredito, e "instável" sem
 * ninguém medindo seria uma faixa que nunca apaga.
 */
export function resetBackendReachability(): void {
  if (graceTimer !== undefined) {
    clearTimeout(graceTimer);
    graceTimer = undefined;
  }
  backendReachable = true;
  commit();
}

export function subscribeConnectivity(listener: Listener): () => void {
  ensureBrowserListener();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function readConnectivity(): ConnectivitySnapshot {
  return snapshot;
}

const serverSnapshot: ConnectivitySnapshot = {
  status: "online",
  browserOnline: true,
  backendReachable: true,
  episode: 0,
};

export function useConnectivity(): ConnectivitySnapshot {
  return useSyncExternalStore(subscribeConnectivity, readConnectivity, () => serverSnapshot);
}

/** Só para testes: volta o módulo ao estado de import. */
export function __resetConnectivityForTests(): void {
  if (graceTimer !== undefined) clearTimeout(graceTimer);
  graceTimer = undefined;
  browserOnline = readOnlineStatus();
  backendReachable = true;
  episode = 0;
  snapshot = derive();
}
